// @ts-strict-ignore
import * as React from 'react';
import { VirtualElement } from '@floating-ui/react';
import isEmpty from 'lodash/isEmpty';
import { useEffect, useState } from 'react';
import { Theme, TrackName } from 'common/components/data-table/types';
import { EmptyDataReact } from 'common/charts/misc/empty-data';
import { DataTableReact } from 'common/components/data-table';
import { ProjectFeatureFlag, REPORT_LIMITS, ReportName } from 'common/report/constants';
import { ContextMenuReact } from 'common/widgets/context-menu';
import { isCustomCsvLimitEnabled } from 'common/widgets/csv-export-modal/utils';
import { getInsightsAggregateCsvExportLimit, getProjectId, getWorkspaceId, isPublicMode } from 'common/report/util';
import { InsightsQueryResults } from 'common/report/insights/util/types';
import { InsightsBookmarkParams, SortConfig } from 'common/types/reports/bookmark';
import { DOWNLOAD_ROW_LIMIT } from 'common/report/insights/util/chart/constants';
import { withStyles } from 'common/util/react';
import { DataGroup } from 'common/widgets/data-group-selector/types';
import { dataGroupStore } from 'common/report/stores/data-groups';
import { CellClickEvent, ColumnResizeEvent, Columns, ColumnWidthOverrides, CommonDataTableFields, SegmentMetadataNode, SortChangeEvent, TableColumnType, TableRow } from 'common/components/data-table/types';
import { Themes } from 'common/components/data-table/themes';
import { IronContextRegistry } from 'common/report/context-registry';
import type { PopoverActionCallback, PopoverCallback } from 'common/charts/types';
import { flattenColumns } from 'common/components/data-table/utils';
import { useCurrentProject } from 'common/contexts/react/current-project-context';
import { useMetrics } from 'common/contexts/react/metrics-resource-context';
import { useTranslation } from 'common/i18n/hooks';
import { useBehaviors } from 'common/contexts/react/behaviors-context';
import { CurrentProject } from 'common/report/model-resources/projects';
import { SegmentFilterController } from 'common/charts/contexts/segment-filter-controller';
import { customEventsStore } from 'common/report/stores/custom-events';
import { useFeatureFlag } from 'common/hooks/useFeatureFlag';
import styles from './index.module.styl';
import css from './index.module.styl?inline';
import { getTableData, InsightsTableColumnContext } from './utils';

/** when set, identifies a click event to a specific metric column */
type PopoverState = {
  metricKey: string | null;
  isTimeComparisonMetric: boolean;
  path: string[];
  targetCell?: HTMLElement;
  columnType: TableColumnType;
  columnIndex?: number;
  rowIndex?: number;
  virtualElementPosition: VirtualElement;
};
interface InsightsTableChartProps {
  canUseViewN?: boolean;
  bookmarkParams: InsightsBookmarkParams | null;
  results: InsightsQueryResults | null;
  /** dataGroups will automatically be obtained from Stores without this param,
   *  but this param is available for environments where Stores aren't supported (eg. Public Dashboards) */
  dataGroups?: Record<string, DataGroup>;
  downloadMode?: boolean;
  resizingLayout?: boolean;
  startScrolledRight?: boolean;

  /** provides actionable items on clicked table cells */
  popoverCallback?: PopoverCallback;
  popoverActionCallback?: PopoverActionCallback;
  segmentFilterController: SegmentFilterController;
  sortChangeCallback?: (options: SortChangeEvent[`detail`]) => void;
  columnResizeCallback?: (ev: ColumnResizeEvent) => void;
  columnWidthOverrides?: ColumnWidthOverrides;
}
export const InsightsTableChart: React.FC<Pick<IronContextRegistry, `behaviors` | `currentProject` | `i18n` | `metrics`> & InsightsTableChartProps> = props => {
  var _props$results, _props$bookmarkParams, _props$columnWidthOve;
  const [rows, setRows] = useState<TableRow[]>();
  const [columnGroups, setColumnGroups] = useState<Columns<any, InsightsTableColumnContext>>();
  const [segmentMetadata, setSegmentMetadata] = useState<SegmentMetadataNode>();
  const [sortConfig, setSortConfig] = useState<SortConfig>();
  const [segmentationLimit, setSegmentationLimit] = useState<number>();
  const [isDownloadTruncated, setIsDownloadTruncated] = useState<boolean>(false);
  const [popoverState, setPopoverState] = useState<PopoverState>();
  const hasFirstClassMetrics = useFeatureFlag(ProjectFeatureFlag.FirstClassMetrics);
  const currentProject: CurrentProject | undefined = isPublicMode() ? undefined : props.currentProject.get().value;
  const hasRequiredParams = (params: InsightsTableChartProps): boolean => {
    return !!(params.bookmarkParams && params.results);
  };
  const updateChartData = (params: InsightsTableChartProps) => {
    if (hasRequiredParams(params)) {
      var _params$results$meta$;
      const {
        bookmarkParams
      } = params;
      const dataGroups = params.dataGroups || dataGroupStore.value;
      const {
        columnGroups: updatedColumnGroups,
        rows: updatedRows,
        segmentMetadata: updatedSegmentMetadata,
        defaultSortConfig,
        sortConfig: updatedSortConfig
      } = getTableData({
        results: params.results,
        bookmarkParams,
        dataGroups,
        canUseViewN: params.canUseViewN,
        hasFirstClassMetrics,
        metricResource: props.metrics,
        behaviorResource: props.behaviors,
        i18n: props.i18n
      });
      const updatedSegmentationLimit = (_params$results$meta$ = params.results.meta.queryLimits) === null || _params$results$meta$ === void 0 ? void 0 : _params$results$meta$.limit;
      let downloadTruncatedRows;
      if (params.downloadMode && updatedRows.length > DOWNLOAD_ROW_LIMIT) {
        downloadTruncatedRows = updatedRows.slice(0, DOWNLOAD_ROW_LIMIT);
      }
      const updatedIsDownloadTruncated = Boolean(downloadTruncatedRows);
      setColumnGroups(updatedColumnGroups);
      setRows(updatedIsDownloadTruncated ? downloadTruncatedRows : updatedRows);
      setSegmentMetadata(updatedSegmentMetadata);
      setIsDownloadTruncated(updatedIsDownloadTruncated);
      setSortConfig(updatedSortConfig);
      setSegmentationLimit(updatedSegmentationLimit);

      /**
       * TODO @pablofierro
       *
       * Table data is computed entirely on the lowest component in the report visualization hierarchy (chart widgets), this leads to
       * data dependencies going upwards and downwards from chart widgets -> ReportVisualization -> SegmentFilterDataTable, in order
       * to have a global table sorting component, we need a reference to the table data being presented to the user, that's why we
       * call `segmentFilterController.loadSections` but this is not an ideal data flow since this `InsightsTableChart` widget is
       * itself rendering the table component and not reusing the `ReportVisualization` abstractions to render a data table.
       *
       * The above seems to break the abstractions we made for `ReportVisualization` being responsible for rendering
       * and displaying table data, a follow up is: how can we improve data flow dependencies so that `ReportVisualization`
       * has access to the table data a report needs (for a global sorting component) without doing cross-component communication
       * with this `segmentFilterController` context call below
       */
      props.segmentFilterController.loadSections([{
        items: [],
        metadata: {},
        tableColumns: updatedColumnGroups,
        apiResultsTruncated: false
      }], {
        defaultSortConfig,
        tableSortConfig: updatedSortConfig,
        defaultNumSelections: 0,
        maxNumSelections: 0
      });
    }
  };
  useEffect(() => {
    props.metrics.subscribe(() => {
      updateChartData(props);
    });
    const projectId = getProjectId();
    const workspaceId = getWorkspaceId();
    if (!props.dataGroups) {
      dataGroupStore.fetch({
        projectId,
        workspaceId
      });
    }
    if (!isPublicMode()) {
      // Refresh table data so the custom event names are refreshed.
      customEventsStore.subscribe(() => {
        updateChartData(props);
      });
      // Make sure we load custom events store for headers for custom events so
      // we can display the names.
      customEventsStore.fetch({
        projectId,
        workspaceId
      });
    }
    updateChartData(props);
  }, []);
  useEffect(() => {
    updateChartData(props);
  }, [props.bookmarkParams, props.dataGroups, props.results]);
  const handleCellClick = (ev: CellClickEvent<any, InsightsTableColumnContext>) => {
    var _clickedColumn$contex, _clickedColumn$contex2;
    const {
      row,
      target,
      context,
      column: clickedColumn
    } = ev.detail;
    // construct segmentation path for row, avoids any issues with comma characters in the segment values
    const path = props.results.headers.slice(1).map((_, index) => row["".concat(CommonDataTableFields.SegmentPrefix).concat(index)]);
    const dataColumns = flattenColumns(columnGroups).filter(column => column.type === TableColumnType.Data);
    let metricKey = (_clickedColumn$contex = clickedColumn.context) === null || _clickedColumn$contex === void 0 ? void 0 : _clickedColumn$contex.metricKey;

    // for single metric tables, allow popover actions on the main metric for the query
    if (metricKey === undefined && dataColumns.length === 1) {
      metricKey = dataColumns[0].context.metricKey;
    }
    setPopoverState({
      path,
      metricKey,
      isTimeComparisonMetric: (_clickedColumn$contex2 = clickedColumn.context) === null || _clickedColumn$contex2 === void 0 ? void 0 : _clickedColumn$contex2.isTimeComparison,
      targetCell: target,
      columnIndex: context.columnIdx,
      rowIndex: context.rowIdx,
      columnType: clickedColumn.type,
      virtualElementPosition: {
        contextElement: target,
        getBoundingClientRect() {
          const targetRect = target.getBoundingClientRect();
          const rowHeight = Themes[Theme.Standalone].rowHeight;

          // for anything greater than 4 row sizes we will present the
          // context menu in the position of the cursor, to prevent
          // the entry point for the menu changing considerably
          if (targetRect.height >= rowHeight * 4) {
            return {
              width: 0,
              height: 0,
              left: ev.detail.mouseX,
              top: ev.detail.mouseY
            } as ClientRect;
          }

          // otherwise we present the context menu following the cursor along
          // the horizontal axis of table rows, but anchored to a specific table cell
          // along the vertical axis
          return {
            height: targetRect.height,
            width: 0,
            left: ev.detail.mouseX,
            top: targetRect.bottom
          } as ClientRect;
        }
      }
    });
  };
  const handlePopoverClose = () => {
    setPopoverState(null);
  };
  const getPopoverMetricPath = () => {
    const {
      metricKey,
      path
    } = popoverState;
    return [metricKey !== null && metricKey !== void 0 ? metricKey : "$event", ...path];
  };
  const handlePopoverAction = ev => {
    const {
      isTimeComparisonMetric,
      columnIndex,
      columnType
    } = popoverState;
    props.popoverActionCallback({
      columnType,
      columnIndex: columnType === TableColumnType.Segment ? columnIndex : null,
      segment: getPopoverMetricPath(),
      action: ev.detail.action,
      isTimeComparisonSegment: isTimeComparisonMetric,
      color: null
    });
    handlePopoverClose();
  };
  const getPopoverMenuItems = () => {
    return props.popoverCallback(null, getPopoverMetricPath(), {
      forSegmentColumn: popoverState.metricKey === undefined,
      isTimeComparisonSegment: popoverState.isTimeComparisonMetric
    });
  };
  if (isEmpty((_props$results = props.results) === null || _props$results === void 0 ? void 0 : _props$results.series)) {
    return <div className={styles.root}>
        <EmptyDataReact />
      </div>;
  }
  if (!hasRequiredParams(props) || !rows) {
    return <div className={styles.root} />;
  }
  return <div className={styles.root} data-sentry-component="InsightsTableChart" data-sentry-source-file="index.tsx">
      <DataTableReact elref={"dataTable"}
    // No special column width persistence on boards yet
    dataTableParams={{
      columnGroups,
      rows,
      segmentMetadata
    }} exportSegmentationLimit={isCustomCsvLimitEnabled(currentProject) ? getInsightsAggregateCsvExportLimit(currentProject) : REPORT_LIMITS[ReportName.Insights].csvExport} isPivotView liftComparisonType={(_props$bookmarkParams = props.bookmarkParams.liftComparison) === null || _props$bookmarkParams === void 0 ? void 0 : _props$bookmarkParams.type} onCellClick={props.popoverCallback ? handleCellClick : null} onColumnResize={ev => {
      var _props$columnResizeCa;
      return (_props$columnResizeCa = props.columnResizeCallback) === null || _props$columnResizeCa === void 0 ? void 0 : _props$columnResizeCa.call(props, ev);
    }} onSortChange={ev => props.sortChangeCallback(ev.detail)} resizingLayout={props.resizingLayout} segmentationLimit={segmentationLimit} selectedCell={popoverState ? {
      columnIdx: popoverState === null || popoverState === void 0 ? void 0 : popoverState.columnIndex,
      rowIdx: popoverState === null || popoverState === void 0 ? void 0 : popoverState.rowIndex
    } : null} sorting={sortConfig} startScrolledRight={props.startScrolledRight} theme={Theme.Standalone} trackName={TrackName.InsightsTable} {...props.resizingLayout ? {
      columnWidthOverrides: (_props$columnWidthOve = props.columnWidthOverrides) !== null && _props$columnWidthOve !== void 0 ? _props$columnWidthOve : {}
    } : {}} data-sentry-element="DataTableReact" data-sentry-source-file="index.tsx" />
      {isDownloadTruncated ? <div className={styles.truncatedDataDownloadText}>
          Results have been truncated to the first 30 results for export.
        </div> : null}

      {popoverState && <ContextMenuReact menuItems={getPopoverMenuItems()} open positionParent={popoverState.virtualElementPosition} onAction={(ev: CustomEvent) => handlePopoverAction(ev)} onClose={() => handlePopoverClose()} />}
    </div>;
};
export const InsightsTableChartReact = withStyles((props: InsightsTableChartProps) => {
  const behaviors = useBehaviors();
  const currentProject = useCurrentProject();
  const i18n = useTranslation();
  const metrics = useMetrics();
  return <InsightsTableChart behaviors={behaviors} currentProject={currentProject} i18n={i18n} metrics={metrics} {...props} />;
}, css);